
import React, { useEffect, useRef, useState } from 'react';
import { SearchIcon } from './icons/PlaybackIcons';
import { getCartoTileUrl } from '../utils/mapUtils';

// Leaflet global
declare const L: any;

interface PrivacyZoneMapProps {
  lat: number;
  lon: number;
  radiusMiles: number;
  onLocationChange: (lat: number, lon: number) => void;
  onRadiusChange: (miles: number) => void;
}

const PrivacyZoneMap: React.FC<PrivacyZoneMapProps> = ({
  lat, lon, radiusMiles, onLocationChange, onRadiusChange
}) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const circleRef = useRef<any>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  // Constants
  const METERS_PER_MILE = 1609.34;
  const radiusMeters = radiusMiles * METERS_PER_MILE;

  // Initialization
  useEffect(() => {
    if (!mapContainerRef.current) return;
    if (mapRef.current) return;

    // Default to US center or provided coordinates
    const startCenter: [number, number] = (lat && lon) ? [lat, lon] : [39.8283, -98.5795];
    const startZoom = (lat && lon) ? 14 : 4;

    const map = L.map(mapContainerRef.current, { zoomControl: false }).setView(startCenter, startZoom);
    L.control.zoom({ position: 'bottomright' }).addTo(map);
    
    L.tileLayer(getCartoTileUrl('rastertiles/voyager'), {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
      maxZoom: 20
    }).addTo(map);

    mapRef.current = map;

    // Handle Map Click
    map.on('click', (e: any) => {
        onLocationChange(e.latlng.lat, e.latlng.lng);
    });

    return () => {
        map.remove();
        mapRef.current = null;
    }
  }, []); // Run once

  // Update Visuals (Marker & Circle)
  useEffect(() => {
      const map = mapRef.current;
      if (!map) return;

      if (lat && lon) {
          const latLng = [lat, lon];

          // Update/Create Marker
          if (markerRef.current) {
              markerRef.current.setLatLng(latLng);
          } else {
              const icon = L.divIcon({
                  className: 'privacy-zone-marker',
                  html: `<div style="background-color: #ef4444; width: 24px; height: 24px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.5);"></div>`,
                  iconSize: [24, 24],
                  iconAnchor: [12, 12]
              });

              markerRef.current = L.marker(latLng, { draggable: true, icon }).addTo(map);
              
              markerRef.current.on('dragend', (e: any) => {
                  const newPos = e.target.getLatLng();
                  onLocationChange(newPos.lat, newPos.lng);
              });
          }

          // Update/Create Circle
          if (circleRef.current) {
              circleRef.current.setLatLng(latLng);
              circleRef.current.setRadius(radiusMeters);
          } else {
              circleRef.current = L.circle(latLng, {
                  color: '#ef4444',
                  fillColor: '#ef4444',
                  fillOpacity: 0.3,
                  radius: radiusMeters,
                  weight: 1
              }).addTo(map);
          }

          // Center map if needed (only on big jumps, simplified here to ignore small drags)
          // map.panTo(latLng); 
      }
  }, [lat, lon, radiusMeters, onLocationChange]);


  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setIsSearching(true);
    setSearchError(null);

    try {
        const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}`);
        if (!response.ok) throw new Error("Search failed");
        
        const data = await response.json();
        
        if (data && data.length > 0) {
            const result = data[0];
            const newLat = parseFloat(result.lat);
            const newLon = parseFloat(result.lon);
            
            onLocationChange(newLat, newLon);
            
            if (mapRef.current) {
                mapRef.current.flyTo([newLat, newLon], 14, { duration: 1.5 });
            }
        } else {
            setSearchError("Address not found.");
        }
    } catch (err) {
        console.error(err);
        setSearchError("Connection failed.");
    } finally {
        setIsSearching(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
          e.preventDefault();
          e.stopPropagation();
          handleSearch();
      }
  };

  return (
    <div className="flex flex-col h-full gap-4">
        {/* Controls */}
        <div className="flex flex-col gap-3 bg-gray-700/50 p-3 rounded-lg border border-gray-600">
            {/* Search */}
            <div className="flex gap-2">
                <div className="flex-grow relative">
                    <input 
                        type="text" 
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Search address (e.g. Home, Work)"
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-md text-white text-sm focus:outline-none focus:ring-1 focus:ring-cyan-500 placeholder-gray-500"
                    />
                    {searchError && (
                        <span className="absolute right-2 top-2.5 text-xs text-red-400 font-medium">{searchError}</span>
                    )}
                </div>
                <button 
                    type="button"
                    onClick={handleSearch}
                    disabled={isSearching}
                    className="px-4 py-2 bg-gray-600 text-white hover:bg-cyan-600 rounded-md transition-colors disabled:opacity-50"
                >
                    {isSearching ? '...' : <SearchIcon className="w-4 h-4" />}
                </button>
            </div>

            {/* Radius Slider */}
            <div className="flex items-center gap-4">
                <label className="text-sm font-medium text-gray-300 min-w-[60px]">Radius:</label>
                <input 
                    type="range"
                    min="0.1"
                    max="2.0"
                    step="0.1"
                    value={radiusMiles}
                    onChange={(e) => onRadiusChange(parseFloat(e.target.value))}
                    className="flex-grow h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer accent-red-500"
                />
                <span className="text-sm font-bold text-white min-w-[50px] text-right">{radiusMiles.toFixed(1)} mi</span>
            </div>
        </div>

        {/* Map */}
        <div className="flex-grow relative min-h-[300px] bg-gray-800 rounded-lg overflow-hidden border border-gray-600">
            <div ref={mapContainerRef} className="w-full h-full" />
            
            {/* Overlay Info */}
            <div className="absolute top-2 right-2 bg-gray-900/80 text-xs text-gray-300 p-2 rounded backdrop-blur-sm z-[1000] border border-gray-700">
                Click map or drag marker to set center
            </div>
        </div>
    </div>
  );
};

export default PrivacyZoneMap;
